import { AGENT, askAgent } from './agent';

export interface ProcessReading {
  pid: number;
  parentPid: number;
  started: string;
  name: string;
  cpuPercent: number | null;
  memoryMb: number;
  ioMbps: number | null;
  gpuPercent: number | null;
  vramMb: number | null;
  responding: boolean | null;
  threads: number;
  handles: number;
}

export interface ProcessSnapshot {
  sampledAt: string;
  intervalSeconds: number;
  cpuBusyPercent: number | null;
  cpuUtilityPercent: number | null;
  memoryUsedMb: number | null;
  memoryTotalMb: number | null;
  gpuAdapter?: string | null;
  gpuMemoryUsedMb?: number | null;
  gpuMemoryTotalMb?: number | null;
  processes: ProcessReading[];
}

export type ProcessMeasure = 'cpuPercent' | 'memoryMb' | 'ioMbps' | 'vramMb';
export type ProcessSort = ProcessMeasure | 'gpuPercent' | 'name' | 'pid' | 'responding';

export const PROCESS_MEASURES: { key: ProcessMeasure; label: string; total: string; colour: string }[] = [
  { key: 'cpuPercent', label: 'CPU', total: 'Attributed CPU', colour: '#22d3ee' },
  { key: 'memoryMb', label: 'RAM', total: 'Private memory', colour: '#be9cfa' },
  { key: 'vramMb', label: 'GPU memory', total: 'Attributed video memory', colour: '#f1c568' },
  { key: 'ioMbps', label: 'I/O', total: 'Process I/O', colour: '#72d8a4' },
];

export function readProcessMeasure(): ProcessMeasure {
  try {
    const stored = globalThis.localStorage?.getItem('systemchart.process.resource');
    return PROCESS_MEASURES.find(measure => measure.key === stored)?.key ?? 'cpuPercent';
  } catch { return 'cpuPercent'; }
}

export function saveProcessMeasure(measure: ProcessMeasure): void {
  try { globalThis.localStorage?.setItem('systemchart.process.resource', measure); } catch { }
}

export function processKey(process: Pick<ProcessReading, 'pid' | 'started'>): string {
  return `${process.pid}:${process.started}`;
}

function startedBefore(first: string, second: string): boolean {
  return /^[0-9]{1,20}$/.test(first) && /^[0-9]{1,20}$/.test(second)
    && BigInt(first) > 0n && BigInt(second) > 0n && BigInt(first) <= BigInt(second);
}

export function processContext(process: ProcessReading, snapshot: ProcessSnapshot, recentCpu: readonly number[] = []) {
  const parent = snapshot.processes.find(candidate => candidate.pid === process.parentPid && candidate.pid !== process.pid
    && startedBefore(candidate.started, process.started)) ?? null;
  const children = snapshot.processes.filter(candidate => candidate.parentPid === process.pid && candidate.pid !== process.pid
    && startedBefore(process.started, candidate.started));
  const sameName = snapshot.processes.filter(candidate => candidate.name.toLowerCase() === process.name.toLowerCase()).length;
  const values = recentCpu.filter(value => Number.isFinite(value) && value >= 0).slice(-30);
  const capacity = snapshot.memoryTotalMb;
  const memoryPercent = capacity !== null && Number.isFinite(capacity) && capacity > 0
    && Number.isFinite(process.memoryMb) && process.memoryMb >= 0 ? process.memoryMb / capacity * 100 : null;
  return {
    parent, children, sameName,
    memoryPercent: memoryPercent !== null && Number.isFinite(memoryPercent) ? memoryPercent : null,
    recentCpu: values.length ? { samples: values.length, minimum: Math.min(...values), maximum: Math.max(...values),
      average: values.reduce((sum, value) => sum + value, 0) / values.length } : null,
  };
}

export function orderedProcesses(rows: ProcessReading[], query: string, sort: ProcessSort, ascending: boolean): ProcessReading[] {
  const search = query.trim().toLowerCase();
  return rows.filter(process => `${process.name} ${process.pid}`.toLowerCase().includes(search))
    .sort((left, right) => {
      const first = left[sort];
      const second = right[sort];
      if (first === null) return second === null ? left.pid - right.pid : 1;
      if (second === null) return -1;
      const order = typeof first === 'string' && typeof second === 'string'
        ? first.localeCompare(second) : Number(first) - Number(second);
      return (ascending ? order : -order) || left.name.localeCompare(right.name) || left.pid - right.pid;
    });
}

export interface ResourceFlow {
  name: string;
  value: number;
  count: number;
  other?: boolean;
}

export function processFlows(rows: ProcessReading[], measure: ProcessMeasure, limit = 6): ResourceFlow[] {
  const groups = new Map<string, ResourceFlow>();
  for (const process of rows) {
    const value = process[measure];
    if (value === null || !Number.isFinite(value) || value <= 0) continue;
    const key = process.name.toLowerCase();
    const group = groups.get(key) ?? { name: process.name, value: 0, count: 0 };
    group.value += value;
    group.count++;
    groups.set(key, group);
  }
  const ranked = [...groups.values()].sort((left, right) => right.value - left.value || left.name.localeCompare(right.name));
  if (ranked.length <= limit) return ranked;
  const rest = ranked.slice(limit);
  return [...ranked.slice(0, limit), { name: 'Other processes', other: true,
    value: rest.reduce((sum, group) => sum + group.value, 0), count: rest.reduce((sum, group) => sum + group.count, 0) }];
}

export function processValue(value: number | null | undefined, measure: ProcessMeasure | 'gpuPercent'): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '\u2014';
  if (measure === 'cpuPercent' || measure === 'gpuPercent') return `${value.toFixed(1)}%`;
  if (measure === 'ioMbps') return `${value.toFixed(2)} MB/s`;
  return value >= 1024 ? `${(value / 1024).toFixed(2)} GB` : `${value.toFixed(1)} MB`;
}

export async function fetchProcesses(): Promise<ProcessSnapshot | null> {
  try {
    const response = await askAgent(`${AGENT}/processes`, { cache: 'no-store' });
    return response.ok ? await response.json() as ProcessSnapshot : null;
  } catch {
    return null;
  }
}

/** Preserve visible identity order during an inspection, appending new identities. */
export function holdProcessOrder(rows: ProcessReading[], previous: readonly string[]): ProcessReading[] {
  const rank = new Map(previous.map((key, index) => [key, index]));
  return [...rows].sort((a, b) => (rank.get(processKey(a)) ?? Number.MAX_SAFE_INTEGER) - (rank.get(processKey(b)) ?? Number.MAX_SAFE_INTEGER));
}
